import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import View from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import { Feature } from 'src/replicant/analytics';
import { PetEntity } from 'src/game/logic/EntityManager';
import { PetSpeciesID } from 'src/replicant/pet';
import * as Pet from 'src/replicant/pet';
import { petAnimationNames } from 'src/replicant/pet/PetAssets';
import { LangBitmapFontTextShadowView } from 'src/lib/ui/components/LangBitmapFontTextShadowView';
import { gsap } from 'gsap';
import { user } from 'src/StateObserver';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { ImageScaleView, ImageView } from '@play-co/timestep-core/ui';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import { animDuration, delay, waitForItPromise } from 'src/lib/utils';
import { match2BuruStreakRewards } from 'src/replicant/match2/match2.ruleset';
import { GemsWallet } from 'src/game/components/GemsWallet';
import PopupBasicLazy from 'src/game/components/popups/PopupBasicLazy';
import {
  getSuperLollipopWinStreak,
  getSuperLollipopWinStreakRequired,
} from 'src/replicant/superLollipop/superLollipop.getters';
import Match2GenericStreakLossBar from '../components/Match2GenericStreakLossBar';
import { match2TournamentRuleset } from 'src/replicant/match2/tournament/match2-tournament.ruleset';
import { FlexBoxView } from '@play-co/timestep-core/flexbox';
import { AssetGroup } from '@play-co/timestep-core';
import { Match2SuperLollipopStreakLossBar } from '../components/Match2SuperLollipopStreakLossBar';
import { Match2BuruStreakLossBar } from '../components/Match2BuruStreakLossBar';
import { movesPinchCheck } from 'src/lib/pinch';
import {
  getMatch2CloseButtonX,
  match2CloseButtonY,
} from '../../components/match2PopupCloseButton';

type CloseHandler = (result: boolean) => void;
const ui = uiConfig.buruburu.care.confirmation;

type PopupMatch2MovePinchStreaksOpts = {
  price: number;
  isSuperLollipopActive?: boolean;
  tournamentStreakIndex?: number;
  buruStreakIndex?: number;
};

const maxStreakItemsToDisplay = 2;

export default class PopupMatch2MovePinchStreaks extends PopupBasicLazy {
  private close: CloseHandler;
  private contentWrapper: View;
  private ctaButton: ButtonScaleViewWithText;
  private gemsWallet: GemsWallet;
  private pet: PetEntity;
  private species: PetSpeciesID;
  private bubble: ImageScaleView;
  private streaks: FlexBoxView;
  private toggleMessage?: LangBitmapFontTextShadowView;
  private isPopupHidden = false;

  public getAssetsGroup() {
    return new AssetGroup([
      ...AssetGroup.constructURLs(['assets/ui/match2/common/']),
    ]);
  }

  constructor(opts: { superview: View; close: CloseHandler }) {
    const width = 676;
    const height = 574;

    super({
      superview: opts.superview,
      close: () => opts.close(false),
      ...ui.popup,
      darkerBg: true,
      offsetY: -50,
      width,
      height,
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.PURCHASE_PINCH_POPUP,
      },
    });

    this.close = opts.close;
    this.buttonClose.removeFromSuperview();
    this.message.removeFromSuperview();
    this.title.title.updateOpts({ color: '#E25959' });
    this.bg.updateOpts({
      backgroundColor: 'rgba(71,24,67,0.95)',
    });
    this.bg.onClick = () => this.toggleShowPuzzle();

    // Create gems header panel
    this.gemsWallet = new GemsWallet({
      superview: this.root,
      tracking: { feature: 'header', $subFeature: 'buy-gems' },
    });

    // Create close button
    new ButtonScaleView({
      ...uiConfig.buttons.close,
      superview: this.box,
      x: getMatch2CloseButtonX(this.box.style.width),
      y: match2CloseButtonY,
      onClick: async () => {
        sounds.playSound('cancel');
        return opts.close(false);
      },
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.PURCHASE_PINCH_BACK,
      },
    });

    // Create a wrapper view for the content
    this.contentWrapper = new View({
      superview: this.box,
      width: this.box.style.width,
      height: this.box.style.height,
    });

    // Create animated pet avatar
    this.species = Pet.getters.getCurrentEvolution(user())?.species ?? 'ball';
    this.pet = new PetEntity('minigame', this.species);
    this.pet.updateOpts({
      superview: this.contentWrapper,
      x: 120,
      y: 365,
      scale: 0.75,
      anchorX: 0.5 * this.pet.style.width,
      anchorY: 0.78 * this.pet.style.height,
    });

    // Create speech bubble
    this.bubble = new ImageScaleView({
      superview: this.contentWrapper,
      image: `assets/ui/conversation/speechbubble.png`,
      imgScale: 0.66,
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 44, right: 44 },
        vertical: { top: 44, bottom: 44 },
      },
      width: 423,
      height: 309,
      x: 220,
      y: 100,
    });

    // Create speech bubble tail
    new ImageView({
      superview: this.bubble,
      image: 'assets/ui/conversation/speechbubble_tail.png',
      width: 82,
      height: 67,
      y: 200,
      x: 10,
      r: Math.PI / 2,
      scale: 0.5,
    });

    // Create speech bubble text
    new LangBitmapFontTextView({
      superview: this.bubble,
      size: 32,
      font: 'Body',
      color: '#985C25',
      localeText: () => i18n('match2.movesPinch.messageGeneric1'),
      width: this.bubble.style.width,
      height: 85,
      y: 12,
      align: 'center',
      verticalAlign: 'center',
      wordWrap: true,
    });

    // Create streak items container
    this.streaks = new FlexBoxView({
      superview: this.bubble,
      width: this.bubble.style.width - 20,
      height: this.bubble.style.height - 100,
      flex: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        paddingBottom: 25,
      },
      x: 10,
      y: 85,
    });

    // Create CTA button
    this.ctaButton = new ButtonScaleViewWithText({
      ...ui.okButton,
      superview: this.contentWrapper,
      width: 477,
      height: 110,
      centerOnOrigin: true,
      x: width * 0.5,
      y: height - 95,
      fontSize: 50,
      icon: {
        image: 'assets/ui/hud/icons/hud_gem.png',
        width: 54,
        height: 45,
        offset: { x: 190, y: 0 },
      },
      labelOffsetX: -35,
      localeText: () => i18n('match2.movesPinch.cta'),
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.PURCHASE_PINCH_OK,
      },
    });

    // Create floating toggle message
    this.toggleMessage = new LangBitmapFontTextShadowView({
      superview: this.root,
      font: 'Body',
      color: '#FFFFFF',
      width: this.root.style.width,
      height: 100,
      size: 30,
      x: this.root.style.width * 0.5,
      y: 150 + this.box.style.y + this.box.style.height * 0.5,
      localeText: () => i18n('match2.movesPinch.toggleMessage1'),
      centerOnOrigin: true,
      centerAnchor: true,
      verticalAlign: 'center',
      align: 'center',
      scaleY: 0,
      blockEvents: true,
      backgroundColor: 'rgba(0,0,0,0.7)',
    });
  }

  public init(args: PopupMatch2MovePinchStreaksOpts) {
    super.init({ title: i18n(`match2.movesPinch.titleGeneric`) });

    // Update the pet avatar animation
    const petAnimations = petAnimationNames[this.species];
    this.pet.setAnimation(petAnimations.cry);

    const { price } = args;

    // Update CTA button label and onClick handler
    this.ctaButton.label.localeText = () =>
      `${i18n('match2.movesPinch.cta')}  ${price}`;
    this.ctaButton.onClick = async () => {
      sounds.sfx.play('ok');
      const success = await movesPinchCheck(price, 'match2_shop_moves');
      if (success) this.close(true);
    };

    // Add streak items
    this.addStreakItems(args);
  }

  private addStreakItems(args: PopupMatch2MovePinchStreaksOpts) {
    const { isSuperLollipopActive, tournamentStreakIndex, buruStreakIndex } =
      args;
    const streakItems: {
      view: View;
      animate: () => void;
    }[] = [];

    // Priority [1] - Add super lollipop item if there will be progress loss.
    if (isSuperLollipopActive) {
      streakItems.push(this.getSuperLollipopItem());
    }

    // Priority [2] - Add tournament item if there will be progress loss,
    // and there is still a space for it.
    if (streakItems.length < maxStreakItemsToDisplay) {
      if (tournamentStreakIndex) {
        streakItems.push(this.getTournamentStreakItem(tournamentStreakIndex));
      }
    }

    // Priority [3] - Add Buru streak item if there will be progress loss,
    // and there is still a space for it.
    if (streakItems.length < maxStreakItemsToDisplay) {
      if (buruStreakIndex) {
        streakItems.push(this.getBuruStreakItem(buruStreakIndex));
      }
    }

    // Add streak items to the view in order and animate them.
    streakItems.forEach((item) => {
      const { view, animate } = item;
      view.updateOpts({ superview: this.streaks });
      waitForItPromise(400).then(() => animate());
    });
  }

  private async toggleShowPuzzle() {
    if (!this.toggleMessage) return;
    this.isPopupHidden = !this.isPopupHidden;

    // Toggle CTAs interactivity accordingly
    this.box.updateOpts({ blockEvents: this.isPopupHidden });

    const duration = 0.5;
    gsap.to(this.bg.style, {
      duration,
      opacity: this.isPopupHidden ? 0 : 1,
      overwrite: true,
    });
    gsap.to(this.box.style, {
      duration,
      opacity: this.isPopupHidden ? 0 : 1,
      overwrite: true,
    });

    await this.tweenToggleMessageScaleY(0);
    this.toggleMessage.localeText = () =>
      i18n(`match2.movesPinch.toggleMessage${this.isPopupHidden ? 2 : 1}`);
    await this.tweenToggleMessageScaleY(1);
  }

  private async tweenToggleMessageScaleY(v: number) {
    if (!this.toggleMessage) return;
    await gsap.to(this.toggleMessage.style, {
      duration: 0.15,
      scaleY: v,
      ease: 'quad.inOut',
      overwrite: true,
    });
  }

  public override async fadeIn() {
    super.fadeIn();
    await delay(animDuration * 2);
    await this.tweenToggleMessageScaleY(1);
  }

  public override async fadeOut() {
    void this.tweenToggleMessageScaleY(0);
    super.fadeOut();
  }

  private getSuperLollipopItem() {
    const view = new Match2SuperLollipopStreakLossBar({
      currentProgress: getSuperLollipopWinStreak(user()) + 1,
      maxProgress: getSuperLollipopWinStreakRequired(user()),
    });

    return {
      view,
      animate: () => view.animate(),
    };
  }

  private getTournamentStreakItem(index: number) {
    const view = new Match2GenericStreakLossBar({
      icon: 'assets/ui/match2/common/icon_pinch_streak_tournament.png',
      items: match2TournamentRuleset.tournamentMultiplier,
      index,
    });
    view.on('ViewRemoved', () => view.unflash());
    return {
      view: view,
      animate: () => view.flash(),
    };
  }

  private getBuruStreakItem(value: number) {
    const view = new Match2BuruStreakLossBar({
      currentProgress: value,
      maxProgress: match2BuruStreakRewards.length,
    });

    return {
      view,
      animate: () => view.animate(),
    };
  }
}
